<script setup>
import { ref, computed } from 'vue'
import { Head } from '@inertiajs/vue3'
import { useI18n } from 'vue-i18n'
import PublicLayout from '@/Layouts/PublicLayout.vue'
import { platformLabel as platformName } from '@/lib/platformLabel'
import CatalogPlaceholder from '@/Components/Public/CatalogPlaceholder.vue'
import PublicGallery from '@/Components/Public/PublicGallery.vue'
import BrandHero from '@/Components/Public/BrandHero.vue'
import LuxButton from '@/Components/Public/LuxButton.vue'
import BookingRequestForm from '@/Components/Public/BookingRequestForm.vue'
import CheckoutCard from '@/Components/Public/CheckoutCard.vue'
import DateField from '@/Components/Public/DateField.vue'
import { datePart, phuketPickupAt, toPhuketWallClock } from '@/lib/phuketTime'

defineOptions({ layout: PublicLayout })

const { t } = useI18n()
const props = defineProps({
  // Named "pkg", not "package" — `package` is a reserved word in strict-mode
  // JS and cannot be used as a bare identifier in the compiled template.
  pkg:       { type: Object,  required: true },
  base_path: { type: String,  default: '/packages' },
  checkout_enabled: { type: Boolean, default: false }
})

// Request-to-book: its own pickup state, independent of CheckoutCard's
// richer local state (hotel, class, passengers travel in the free-text note).
const today = computed(() => new Date().toLocaleDateString('en-CA')) // YYYY-MM-DD, local
// A pickup needs the hour too — the office dispatches a driver — and the
// same 6h lead time the checkout card asks for.
const requestPickup = ref('')
const minPickup = computed(() => toPhuketWallClock(new Date(Date.now() + 6 * 60 * 60 * 1000)))

const photos = props.pkg.photos?.length ? props.pkg.photos : []

const bookingCtas = computed(() =>
  Object.entries(props.pkg.contact_intents || {}).map(([platform, url]) => ({ platform, url, name: platformName(platform) }))
)
</script>

<template>
  <Head>
    <title>{{ pkg.title }} · {{ t('public.head_title') }}</title>
    <meta name="description" :content="pkg.description">
  </Head>

  <BrandHero kind="package" :eyebrow="t(`public.packages.kind.${pkg.kind}`)" :title="pkg.title">
    <template #top>
      <a href="/transfers" class="bhero-back">← {{ t('public.packages.back') }}</a>
    </template>
    <p class="bhero-price"><span class="fw-value">{{ pkg.price }}</span></p>
    <div v-if="pkg.contact_intents && Object.keys(pkg.contact_intents).length" class="bhero-ctas">
      <LuxButton
        v-for="(url, platform, idx) in pkg.contact_intents"
        :key="platform"
        :href="url"
        :variant="idx === 0 ? 'solid' : 'line'"
        tone="dark"
        :glyph="platform"
        target="_blank"
        rel="noopener"
      >{{ t('public.write_on', { platform: platformName(platform) }) }}</LuxButton>
    </div>
  </BrandHero>

  <section class="body">
    <div class="body__inner">
      <PublicGallery :photos="photos" :alt="pkg.title">
        <template #placeholder>
          <CatalogPlaceholder kind="package" />
        </template>
      </PublicGallery>

      <div class="detail">
        <div class="detail__content">
          <section v-if="pkg.description" class="card">
            <h2 class="section-title">{{ t('public.packages.sections.about') }}</h2>
            <p class="prose">{{ pkg.description }}</p>
          </section>

          <section v-if="pkg.legs && pkg.legs.length" class="card">
            <h2 class="section-title">{{ t('public.packages.sections.included') }}</h2>
            <ol class="legs">
              <li v-for="(leg, i) in pkg.legs" :key="i" class="leg">
                <span class="leg__n fw-value">{{ i + 1 }}</span>
                <div>
                  <p class="leg__title">{{ leg.title }}</p>
                  <p v-if="leg.details" class="leg__details">{{ leg.details }}</p>
                </div>
              </li>
            </ol>
          </section>
        </div>

        <aside class="detail__aside">
          <div class="facts">
            <h2 class="section-title">{{ t('public.packages.fields.price') }}</h2>
            <p class="facts__price fw-value">{{ pkg.price }}</p>
          </div>

          <div class="trust-card">
            <h2 class="section-title">{{ t('public.packages.trust.title') }}</h2>
            <ul class="trust-list">
              <li>{{ t('public.packages.trust.no_payment') }}</li>
              <li>{{ t('public.packages.trust.real_team') }}</li>
              <li>{{ t('public.packages.trust.local_support') }}</li>
            </ul>
          </div>

          <div id="request-to-book" class="plan-card">
            <h2 class="section-title">{{ t('public.packages.booking.title') }}</h2>
            <p v-if="!checkout_enabled" class="plan-note">{{ t('public.packages.booking.note') }}</p>
            <label class="plan-row">
              <span class="plan-label">{{ t('public.checkout.package.pickup_label') }}</span>
              <DateField v-model="requestPickup" type="datetime-local" :min="minPickup" class="plan-date" :placeholder="t('public.pick_datetime')" />
            </label>
            <h3 class="plan-subtitle">{{ t('public.booking_request.title') }}</h3>
            <BookingRequestForm
              bookable-type="package"
              :bookable-id="pkg.id"
              :details="{ start_on: datePart(requestPickup), pickup_at: phuketPickupAt(requestPickup) }"
              :disabled="!requestPickup"
            />
            <p v-if="bookingCtas.length" class="plan-divider">{{ t('public.booking_request.or_chat') }}</p>
            <div v-if="bookingCtas.length" class="plan-ctas">
              <LuxButton v-for="c in bookingCtas" :key="c.platform" :href="c.url" variant="line" tone="light" :glyph="c.platform" block target="_blank" rel="noopener">{{ t('public.packages.booking.cta_via', { platform: c.name }) }}</LuxButton>
            </div>
          </div>

          <CheckoutCard
            v-if="checkout_enabled"
            bookable-type="package"
            :bookable-id="pkg.id"
          />
        </aside>
      </div>
    </div>
  </section>
</template>

<style scoped>
.bhero-back { display: inline-block; margin-bottom: 1rem; color: inherit; opacity: 0.85; text-decoration: none; font-size: 0.95rem; }
.bhero-back:hover { opacity: 1; }
.bhero-price { margin: 1.1rem 0 0; font-size: clamp(1.6rem, 3vw, 2.1rem); }
.bhero-ctas { display: flex; flex-wrap: wrap; gap: 0.6rem; margin-top: 1.5rem; }

.body { background: var(--bg); }
.body__inner { max-width: 96rem; margin: 0 auto; padding: clamp(2rem, 4vw, 3rem) clamp(1.25rem, 4vw, 3rem) 4rem; }
.detail { display: grid; grid-template-columns: 1fr; gap: 1.5rem; margin-top: 1.5rem; }
@media (min-width: 880px) { .detail { grid-template-columns: 1.6fr 0.9fr; gap: 2rem; align-items: start; } }
.detail__content { display: grid; gap: 1rem; }
@media (min-width: 880px) { .detail__aside { position: sticky; top: 1.5rem; } }
.facts { background: var(--surface); border: 1px solid var(--line); border-radius: 1rem; padding: 1.4rem; }
.facts__price { font-size: 1.3rem; margin: 0; color: var(--second); }
.card { background: var(--surface); border: 1px solid var(--line); border-radius: 1rem; padding: 1.4rem; }
.section-title { font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted); margin: 0 0 0.75rem; }
.prose { color: var(--inkSoft); line-height: 1.6; white-space: pre-wrap; margin: 0; }

.legs { list-style: none; margin: 0; padding: 0; display: grid; gap: 1.1rem; }
.leg { display: flex; gap: 0.9rem; align-items: flex-start; }
.leg__n {
  flex: none; width: 1.9rem; height: 1.9rem; display: grid; place-items: center;
  border-radius: 999px; background: var(--second); color: var(--accentInk); font-size: 0.9rem;
}
.leg__title { margin: 0 0 0.15rem; font-weight: 700; color: var(--ink); }
.leg__details { margin: 0; color: var(--inkSoft); line-height: 1.5; }

.trust-card {
  margin-top: 1.25rem; padding: 1.25rem;
  background: var(--surface); border: 1px solid var(--line);
  border-radius: var(--radius-table, 0.75rem);
}
.trust-list { list-style: none; margin: 0.75rem 0 0; padding: 0; display: grid; gap: 0.6rem; }
.trust-list li { position: relative; padding-left: 1.6rem; color: var(--inkSoft); line-height: 1.4; }
.trust-list li::before { content: "✓"; position: absolute; left: 0; top: 0; color: var(--ok); font-weight: 700; }

.plan-card {
  margin-top: 1.25rem; padding: 1.25rem;
  background: var(--surface); border: 1px solid var(--line);
  border-radius: var(--radius-table, 0.75rem);
}
.plan-note { margin: 0 0 0.85rem; color: var(--inkSoft); font-size: 0.9rem; line-height: 1.5; }
.plan-row { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin-top: 0.85rem; }
.plan-label { color: var(--inkSoft); font-size: 0.95rem; }
.plan-date {
  border: 1px solid var(--line); border-radius: var(--radius-input, 0.5rem);
  background: var(--bg); color: var(--ink); padding: 0.4rem 0.6rem; font: inherit;
  color-scheme: light;
}
.plan-subtitle { margin: 1.1rem 0 0; font-size: 0.85rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--muted); }
.plan-divider { margin: 0.9rem 0 0; text-align: center; font-size: 0.8rem; color: var(--muted); }
.plan-ctas { margin-top: 1rem; display: grid; gap: 0.5rem; }
</style>
